/**
 * 🪐 HallOfChampionsPage — Pet of the Week leaderboard + past winners.
 *
 * Auto-resets Mon 00:00 PT. Winners get a permanent badge ("🪐 Pet of Week N · YYYY")
 * + 50 OD + Epic+ egg. Auto-posted to community feed on claim.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ChainLegendBoard from "@/components/ChainLegendBoard";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtCountdown(secs) {
  if (!secs || secs <= 0) return "now";
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function HallOfChampionsPage() {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState(null);
  const [champions, setChampions] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([
        axios.get(`${API}/pet-of-week/state`),
        axios.get(`${API}/hall-of-champions`),
      ]);
      setState(s.data);
      setChampions(h.data?.champions || []);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;
    refresh();
  }, [authLoading, user, refresh]);

  const onClaim = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/pet-of-week/claim`);
      const rw = r.data?.rewards || {};
      toast.success(`🪐 Pet of the Week! +${rw.od} 🪙 OD · +${rw.eggs?.length || 0} 🥚 ${rw.eggs?.join(", ") || ""} · ${rw.title}`, { duration: 9000 });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed.");
    } finally {
      setBusy(false);
    }
  };

  if (authLoading) return null;
  if (!user) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-neutral-200">
        <Header />
        <div className="max-w-3xl mx-auto p-8 text-center text-neutral-400">
          🔒 Sign in to view the Hall of Champions.
        </div>
        <Footer />
      </div>
    );
  }
  if (!state) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-neutral-200">
        <Header />
        <div className="max-w-3xl mx-auto p-8 text-center text-neutral-500">Loading Hall…</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-neutral-200" data-testid="hall-page">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-6 pb-24">
        {/* Hero */}
        <div className="mb-6 rounded-2xl border border-purple-500/40 bg-gradient-to-br from-[#0a0a1a] via-[#0A0A0A] to-[#1a0820] p-5 shadow-[0_0_30px_rgba(168,85,247,0.2)]">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-3xl font-black tracking-wide text-purple-300" style={{ fontFamily: 'Anton, sans-serif' }}>
                🪐 HALL OF CHAMPIONS
              </div>
              <div className="text-xs text-neutral-400 mt-1">
                Most pet-levels gained = Pet of the Week · Resets in <span className="text-purple-300 font-bold">{fmtCountdown(state.seconds_to_reset)}</span>
              </div>
            </div>
            {state.can_claim_previous && (
              <button data-testid="hall-claim-btn" disabled={busy} onClick={onClaim}
                      className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-amber-400 hover:from-purple-400 hover:to-amber-300 disabled:opacity-50 text-black font-bold text-sm shadow-lg">
                🎁 Claim Crown — {state.previous_week_label}
              </button>
            )}
            {state.previous_already_claimed && (
              <span className="px-3 py-1.5 rounded-full bg-neutral-800 text-neutral-400 text-xs" data-testid="hall-already-claimed">
                ✅ Last week claimed
              </span>
            )}
          </div>
          {/* My standing */}
          <div className="mt-4 rounded-lg border border-neutral-800 bg-black/40 p-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm">
                <span className="text-neutral-500">Your pet levels gained this week: </span>
                <span className="font-bold text-purple-200" data-testid="hall-my-levels">{state.my_levels_gained}</span>
              </div>
              {state.my_rank_current ? (
                <span className="px-3 py-1 rounded-full bg-purple-500/20 text-purple-200 text-xs font-bold" data-testid="hall-my-rank">
                  Currently #{state.my_rank_current}
                </span>
              ) : (
                <span className="px-3 py-1 rounded-full bg-neutral-800 text-neutral-400 text-xs">
                  Level up your pet to climb!
                </span>
              )}
            </div>
            {state.my_title && (
              <div className="mt-2 text-xs text-amber-300 font-bold" data-testid="hall-my-title">
                {state.my_title}
              </div>
            )}
          </div>
        </div>

        {/* This week's leaderboard */}
        <section className="mb-6" data-testid="hall-current-section">
          <div className="text-xs uppercase tracking-wider text-purple-300 font-bold mb-2">
            🔥 This Week — {state.week_label}
          </div>
          {state.current_top.length === 0 ? (
            <div className="text-center text-neutral-500 text-sm py-6 rounded-lg border border-neutral-800 bg-black/30">
              No pet level-ups yet this week. Start grinding!
            </div>
          ) : (
            <div className="space-y-1.5">
              {state.current_top.map((row) => (
                <LeaderRow key={row.user_id} row={row} testid={`hall-current-row-${row.rank}`} />
              ))}
            </div>
          )}
        </section>

        {/* 🔥 Chain Legends — weekly Extra Spin streak board (Jul 2026) */}
        <ChainLegendBoard />

        {/* Past champions */}
        <section data-testid="hall-champions-section">
          <div className="text-xs uppercase tracking-wider text-amber-300 font-bold mb-2">
            🏆 Past Champions
          </div>
          {champions.length === 0 ? (
            <div className="text-center text-neutral-500 text-sm py-6 rounded-lg border border-neutral-800 bg-black/30">
              No champions crowned yet — be the first!
            </div>
          ) : (
            <div className="space-y-1.5">
              {champions.map((c) => (
                <div key={c.event_id}
                     className="flex items-center justify-between rounded-lg border border-amber-500/30 bg-gradient-to-r from-amber-900/10 to-transparent px-3 py-2 text-sm"
                     data-testid={`hall-champ-${c.event_id}`}>
                  <div>
                    <div className="font-bold text-amber-200">{c.title}</div>
                    <div className="text-xs text-neutral-400">
                      {c.display_name}{c.handle && ` · @${c.handle}`} · {c.levels_gained} pet levels
                    </div>
                  </div>
                  <div className="text-2xl">🪐</div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
      <Footer />
    </div>
  );
}

function LeaderRow({ row, testid }) {
  const isPodium = row.rank <= 3;
  const icon = row.rank === 1 ? "🪐" : row.rank === 2 ? "🥈" : row.rank === 3 ? "🥉" : `#${row.rank}`;
  return (
    <div className={`flex items-center justify-between rounded-lg border ${
      row.rank === 1 ? "border-amber-400 shadow-[0_0_18px_rgba(245,158,11,0.4)]" :
      isPodium ? "border-neutral-500" : "border-neutral-800"
    } bg-black/40 px-3 py-2 text-sm`} data-testid={testid}>
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-xl w-8 text-center">{icon}</span>
        <div className="min-w-0">
          <div className="font-bold truncate">
            {row.name}
            {row.handle && <span className="text-neutral-500 text-xs ml-1">@{row.handle}</span>}
          </div>
          <div className="text-[10px] text-neutral-500">
            {row.pet_name} · Lv {row.pet_level}
            {row.pet_of_week_title && <span className="text-amber-400 ml-1">· {row.pet_of_week_title}</span>}
          </div>
        </div>
      </div>
      <div className="text-purple-300 font-bold">+{row.levels_gained} Lv</div>
    </div>
  );
}
